import { useCallback, useEffect, useRef, useState } from "react";

import {
  isCanceledError,
  IWaitingListPassengerDto,
  IWaitingListQueueResponseDto,
  WaitingListApi,
} from "@ryde/shared";

export interface IUseWaitingListResult {
  queue: IWaitingListQueueResponseDto | undefined;
  isLoading: boolean;
  fetchSnapshot: () => Promise<void>;
  sendInvitation: (passengerId: number) => Promise<boolean>;
}

export const useWaitingList = ({
  rideId,
  isEnabled,
}: {
  rideId: number | undefined;
  isEnabled: boolean;
}): IUseWaitingListResult => {
  const [queue, setQueue] = useState<IWaitingListQueueResponseDto | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const fetchSnapshot = useCallback(async (): Promise<void> => {
    if (!isEnabled || !rideId) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    try {
      const res = await WaitingListApi.getQueue(rideId, controller);
      if (res?.success && !controller.signal.aborted) {
        setQueue(res.data);
      }
    } catch (error) {
      if (!isCanceledError(error)) {
        throw error;
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [isEnabled, rideId]);

  const sendInvitation = useCallback(
    async (passengerId: number): Promise<boolean> => {
      if (!rideId) {
        return false;
      }
      const result = await WaitingListApi.sendInvitation({
        rideId,
        passengerId,
      });
      if (!result?.success) {
        return false;
      }
      const invitedAt = result.data?.invitedAt ?? new Date().toISOString();
      const mapRow = (
        row: IWaitingListPassengerDto,
      ): IWaitingListPassengerDto =>
        row.passengerId === passengerId
          ? { ...row, isNotified: true, invitedAt }
          : row;
      setQueue(prev =>
        prev
          ? {
              ...prev,
              waiting: prev.waiting.map(mapRow),
              all: prev.all.map(mapRow),
            }
          : prev,
      );

      return true;
    },
    [rideId],
  );

  useEffect(() => {
    fetchSnapshot();

    return () => abortRef.current?.abort();
  }, [fetchSnapshot]);

  return { queue, isLoading, fetchSnapshot, sendInvitation };
};
